/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import BottomPartForm from './BottomPartForm';
import { LPBSimpleModel } from './Models';
import { LPBSimpleValidation } from './ModelValidators';
import { RECIPIENT_TYPES } from 'components/common/constants';
import i18nContext from 'components/i18n-context';
import { amountFormattedValue, prepareFieldErrors, strToFloat } from 'services/utils';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';

const LPBSimpleForm = ({
  customerNumber,
  calculateCommission,
  checkTransfer,
  uploadDocuments,
  account,
  paymentMethod,
  uploadedFiles,
  commission,
  removePaymentFile,
  error,
  setError,
  isCommissionLoading,
  isLoading,
  isSuccess,
  isRepeatPayment,
  checkIban,
  iban,
  setIban,
  ibanCredentials,
  validationProps,
  commonForm,
  isFormResetAfterAccountChanging,
  setIsPreparedTransactionsCreation,
  isForApprovalTransactionEdit,
  isUserCanCreatePreparedTransaction,
  isUserCanSendTransaction,
  validateForApprovalTransaction
}) => {
  const i18n = useContext(i18nContext);
  const model = LPBSimpleModel;
  const validationSchema = LPBSimpleValidation({ ...validationProps, model });
  const form = useFormik(validationSchema);
  const serverFieldsErrors = prepareFieldErrors(i18n, error);
  const [disabled, setDisabled] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [showMethodCommission, setShowMethodCommission] = useState(false);
  const { values, errors, handleSubmit, handleChange, validateField, validateForm, resetForm, setFieldValue } =
    commonForm || form;

  useEffect(() => {
    if (isRepeatPayment) {
      const asyncOperations = async () => {
        await handleIbanComplete(false);
      };

      asyncOperations();
    }
  }, [account.wallet_number]);

  useEffect(() => {
    if (isSuccess) {
      resetForm({});
      setDisabled(true);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (iban && !values.recipient_wallet) {
      setFieldValue('recipient_wallet', iban);
    }
  }, [iban, values.recipient_wallet]);

  useEffect(() => {
    const asyncOperations = async () => {
      if (!isFormResetAfterAccountChanging) return;

      if ((isRepeatPayment && !values.recipient_wallet) || !isRepeatPayment) {
        if (values.amount) {
          await calculateCommission(customerNumber, {
            walletNumber: account?.wallet_number,
            paymentMethod: paymentMethod,
            amount: strToFloat(values?.amount?.toString() || '')
          });
        }

        if (!isForApprovalTransactionEdit) {
          await handleCheckTransfer(undefined);
        }
      }
    };

    asyncOperations();
  }, [paymentMethod, isRepeatPayment, isFormResetAfterAccountChanging]);

  const handleCheckTransfer = async (fieldName) => {
    if (fieldName) await validateField(fieldName);
    if (
      !serverFieldsErrors?.iban &&
      (values.recipient_wallet || (values.account_number && values.sort_code)) &&
      values.amount &&
      values.recipient_type &&
      values.comment &&
      (values.recipient_type === RECIPIENT_TYPES.COMPANY ? values.company_name : values.first_name && values.last_name)
    ) {
      const errorsResult = await validateForm();

      if (!Object.keys(errorsResult).length) {
        await checkTransfer(
          customerNumber,
          model({
            providerTableData: values,
            data: { wallet: account, paymentMethod, ...ibanCredentials }
          })
        );
        setShowMethodCommission(true);
        setDisabled(false);
      }
    }
  };

  const handleRecipientTypeChange = (name, data) => handleChange({ target: { name, value: data } });
  const onAmountIBANChange = (e) => {
    setShowMethodCommission(false);
    handleChange(e);
    if (e.target.name === 'recipient_wallet') {
      setIban(e.target.value);
    }
  };

  const getCommissionValue = () => {
    if (!commission.value && !commission.currency) {
      return '0.00';
    }
    return `${commission.commissionDetails?.displayAmount || amountFormattedValue(commission.value)}`;
  };

  const handleIbanComplete = async (causedOnBlur) => {
    if (causedOnBlur) await validateField('recipient_wallet');
    values.recipient_wallet && (await checkIban(values.recipient_wallet));
    if (causedOnBlur && !isForApprovalTransactionEdit) await handleCheckTransfer(undefined);
  };

  const bottomPartProps = {
    values,
    errors,
    error,
    handleChange,
    handleSubmit,
    serverFieldsErrors,
    onAmountIBANChange,
    calculateCommission,
    handleCheckTransfer,
    getCommissionValue,
    paymentMethod,
    isSubmitDisabled:
      (isForApprovalTransactionEdit ? false : disabled) ||
      isLoading ||
      isCommissionLoading ||
      !!Object.keys(errors).length ||
      !!Object.keys(serverFieldsErrors).length ||
      !!error ||
      !paymentMethod,
    isLoading: isLoading || isCommissionLoading,
    uploadedFiles,
    uploadDocuments,
    removePaymentFile,
    customerNumber,
    currentWalletNumber: account?.wallet_number,
    commission,
    setIsPreparedTransactionsCreation,
    isForApprovalTransactionEdit,
    isUserCanCreatePreparedTransaction,
    isUserCanSendTransaction,
    validateForApprovalTransaction,
    model: LPBSimpleModel,
    setError
  };

  return (
    <>
      <div className={'inputs-wrapper'}>
        <Input
          isRequired={true}
          isDisabled={!!values.account_number || !!values.sort_code}
          autoComplete={'off'}
          label={i18n.getMessage('transfer.form.recipientIban.label')}
          name={'recipient_wallet'}
          value={values.recipient_wallet}
          error={
            (!values.account_number && errors.recipient_wallet) ||
            serverFieldsErrors.iban ||
            serverFieldsErrors.recipientIban
          }
          onChange={onAmountIBANChange}
          onBlur={() => handleIbanComplete(true)}
          subText={
            ibanCredentials?.bank_name &&
            i18n.getMessage('transfer.form.bankName.subText', { bankName: ibanCredentials?.bank_name })
          }
        />
      </div>
      <div className={`inputs-wrapper ${values.recipient_type}`}>
        <InputDropDown
          label={i18n.getMessage('transfer.form.recipientType.label')}
          autoComplete={'off'}
          name={'recipient_type'}
          value={values.recipient_type}
          onChange={handleRecipientTypeChange}
          options={Object.values(RECIPIENT_TYPES).map((value) => ({ key: value, value }))}
        />
        {values.recipient_type === RECIPIENT_TYPES.COMPANY && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.companyName.label')}
            name={'company_name'}
            value={values.company_name}
            error={errors.company_name || serverFieldsErrors.companyName}
            onChange={handleChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('company_name')}
          />
        )}
        {values.recipient_type === RECIPIENT_TYPES.INDIVIDUAL && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.firstName.label')}
            name={'first_name'}
            value={values.first_name}
            error={errors.first_name || serverFieldsErrors.firstName}
            onChange={handleChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('first_name')}
          />
        )}
        {values.recipient_type === RECIPIENT_TYPES.INDIVIDUAL && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.lastName.label')}
            name={'last_name'}
            value={values.last_name}
            error={errors.last_name || serverFieldsErrors.lastName}
            onChange={handleChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('last_name')}
          />
        )}
      </div>
      <BottomPartForm {...bottomPartProps} />
    </>
  );
};

LPBSimpleForm.propTypes = {
  customerNumber: PropTypes.string,
  calculateCommission: PropTypes.func.isRequired,
  checkTransfer: PropTypes.func.isRequired,
  uploadDocuments: PropTypes.func.isRequired,
  checkIban: PropTypes.func.isRequired,
  iban: PropTypes.string,
  setIban: PropTypes.func,
  commonForm: PropTypes.object,
  ibanCredentials: PropTypes.object,
  account: PropTypes.object,
  paymentMethod: PropTypes.string,
  commission: PropTypes.object,
  isCommissionLoading: PropTypes.bool,
  isLoading: PropTypes.bool,
  isRepeatPayment: PropTypes.bool,
  error: PropTypes.any,
  setError: PropTypes.func,
  uploadedFiles: PropTypes.array,
  removePaymentFile: PropTypes.func,
  isSuccess: PropTypes.bool,
  validationProps: PropTypes.object.isRequired,
  isFormResetAfterAccountChanging: PropTypes.bool,
  setIsFormResetAfterAccountChanging: PropTypes.func,
  setIsPreparedTransactionsCreation: PropTypes.func,
  isForApprovalTransactionEdit: PropTypes.bool,
  isUserCanCreatePreparedTransaction: PropTypes.bool,
  isUserCanSendTransaction: PropTypes.bool,
  validateForApprovalTransaction: PropTypes.func
};

export default LPBSimpleForm;
